import type { CorTabuleiro, NivelClassico, PartidaArquivada, Preferencias, Tema } from '../types';
import { normalizarNivel } from './niveis';

const CHAVE_CONTAS = 'xeque-regio:contas:v1';
const MAX_HISTORICO = 120;

export interface PreferenciasConta {
  tema: Preferencias['tema'];
  corTabuleiro: Preferencias['corTabuleiro'];
  somAtivo: boolean;
  volume: number;
  reduzirMovimento: boolean;
  mostrarDestinos: boolean;
  nivelPreferido: NivelClassico;
  controlePreferido: string;
  minutosPersonalizados?: number;
  incrementoPersonalizado?: number;
}

export interface ContaJogador {
  nickname: string;
  salt: string;
  senhaHash: string;
  criadoEm: number;
  atualizadoEm: number;
  preferencias: PreferenciasConta;
  historico: PartidaArquivada[];
}

interface BancoContas {
  versao: 1;
  contas: Record<string, ContaJogador>;
}

function slugNickname(nome: string): string {
  return nome
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48) || 'anonimo';
}

function bancoVazio(): BancoContas {
  return { versao: 1, contas: {} };
}

function lerBanco(): BancoContas {
  try {
    const raw = localStorage.getItem(CHAVE_CONTAS);
    if (!raw) return bancoVazio();
    const parsed = JSON.parse(raw) as BancoContas;
    if (parsed?.versao !== 1 || typeof parsed.contas !== 'object' || !parsed.contas) return bancoVazio();
    return parsed;
  } catch {
    return bancoVazio();
  }
}

function gravarBanco(banco: BancoContas): void {
  try {
    localStorage.setItem(CHAVE_CONTAS, JSON.stringify(banco));
  } catch {
    /* quota / privado */
  }
}

const TEMAS: Tema[] = ['regio', 'noturno', 'claro'];
const CORES_TAB: CorTabuleiro[] = ['nogueira', 'areia', 'floresta', 'oceano', 'ardosia', 'contraste'];

function preferenciasPadraoConta(): PreferenciasConta {
  return {
    tema: 'regio',
    corTabuleiro: 'nogueira',
    somAtivo: true,
    volume: 0.55,
    reduzirMovimento: false,
    mostrarDestinos: true,
    nivelPreferido: '400',
    controlePreferido: '10-0',
    minutosPersonalizados: 5,
    incrementoPersonalizado: 0
  };
}

function normalizarPreferenciasConta(p?: Partial<PreferenciasConta> | null): PreferenciasConta {
  const base = preferenciasPadraoConta();
  if (!p) return base;
  return {
    tema: TEMAS.includes(p.tema as Tema) ? (p.tema as Tema) : base.tema,
    corTabuleiro: CORES_TAB.includes(p.corTabuleiro as CorTabuleiro) ? (p.corTabuleiro as CorTabuleiro) : base.corTabuleiro,
    somAtivo: p.somAtivo ?? base.somAtivo,
    volume: typeof p.volume === 'number' ? Math.min(1, Math.max(0, p.volume)) : base.volume,
    reduzirMovimento: Boolean(p.reduzirMovimento),
    mostrarDestinos: p.mostrarDestinos !== false,
    nivelPreferido: normalizarNivel(p.nivelPreferido ?? base.nivelPreferido),
    controlePreferido: typeof p.controlePreferido === 'string' && p.controlePreferido ? p.controlePreferido : base.controlePreferido,
    minutosPersonalizados:
      typeof p.minutosPersonalizados === 'number' ? Math.min(180, Math.max(1, Math.round(p.minutosPersonalizados))) : base.minutosPersonalizados,
    incrementoPersonalizado:
      typeof p.incrementoPersonalizado === 'number'
        ? Math.min(60, Math.max(0, Math.round(p.incrementoPersonalizado)))
        : base.incrementoPersonalizado
  };
}

async function hashSenha(senha: string, salt: string): Promise<string> {
  const data = new TextEncoder().encode(`${salt}\0${senha}`);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function saltAleatorio(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function contaParaPreferencias(conta: ContaJogador): Preferencias {
  const p = normalizarPreferenciasConta(conta.preferencias);
  return {
    tema: p.tema,
    corTabuleiro: p.corTabuleiro,
    somAtivo: p.somAtivo,
    volume: p.volume,
    reduzirMovimento: p.reduzirMovimento,
    mostrarDestinos: p.mostrarDestinos,
    nivelPreferido: p.nivelPreferido,
    controlePreferido: p.controlePreferido,
    minutosPersonalizados: p.minutosPersonalizados,
    incrementoPersonalizado: p.incrementoPersonalizado,
    nomeJogador: conta.nickname,
    nomesSalvos: listarNicknames()
  };
}

export function listarNicknames(): string[] {
  const banco = lerBanco();
  return Object.values(banco.contas)
    .map((c) => c.nickname)
    .sort((a, b) => a.localeCompare(b, 'pt'));
}

export function existeConta(nickname: string): boolean {
  return Boolean(lerBanco().contas[slugNickname(nickname)]);
}

export function obterConta(nickname: string): ContaJogador | null {
  return lerBanco().contas[slugNickname(nickname)] ?? null;
}

/**
 * Remove a conta deste aparelho (preferências da conta + histórico da conta).
 * Não pede senha: o storage já é local e o usuário está no próprio navegador.
 */
export function removerConta(nickname: string): boolean {
  const chave = slugNickname(nickname);
  const banco = lerBanco();
  if (!banco.contas[chave]) return false;
  delete banco.contas[chave];
  gravarBanco(banco);
  return true;
}

export type ResultadoAuth =
  | { ok: true; preferencias: Preferencias; conta: ContaJogador }
  | { ok: false; erro: string };

export async function criarConta(nickname: string, senha: string, historicoInicial: PartidaArquivada[] = []): Promise<ResultadoAuth> {
  const nome = nickname.trim().slice(0, 40);
  if (nome.length < 2) return { ok: false, erro: 'O nickname precisa ter pelo menos 2 caracteres.' };
  if (nome.length > 40) return { ok: false, erro: 'Nickname com no máximo 40 caracteres.' };
  if (senha.length < 4) return { ok: false, erro: 'A senha precisa ter pelo menos 4 caracteres.' };
  if (senha.length > 64) return { ok: false, erro: 'Senha com no máximo 64 caracteres.' };

  const chave = slugNickname(nome);
  const banco = lerBanco();
  if (banco.contas[chave]) return { ok: false, erro: 'Esse nickname já está cadastrado neste aparelho.' };

  const salt = saltAleatorio();
  const senhaHash = await hashSenha(senha, salt);
  const agora = Date.now();
  const conta: ContaJogador = {
    nickname: nome,
    salt,
    senhaHash,
    criadoEm: agora,
    atualizadoEm: agora,
    preferencias: preferenciasPadraoConta(),
    historico: historicoInicial.slice(0, MAX_HISTORICO)
  };
  banco.contas[chave] = conta;
  gravarBanco(banco);
  return { ok: true, preferencias: contaParaPreferencias(conta), conta };
}

export async function autenticarConta(nickname: string, senha: string): Promise<ResultadoAuth> {
  const nome = nickname.trim();
  if (nome.length < 2) return { ok: false, erro: 'Informe o nickname.' };
  if (!senha) return { ok: false, erro: 'Informe a senha.' };

  const conta = obterConta(nome);
  if (!conta) return { ok: false, erro: 'Conta não encontrada. Crie um cadastro com esse nickname.' };

  const hash = await hashSenha(senha, conta.salt);
  if (hash !== conta.senhaHash) return { ok: false, erro: 'Senha incorreta.' };

  return { ok: true, preferencias: contaParaPreferencias(conta), conta };
}

/** Restaura preferências da conta se a sessão ainda estiver ativa (sem pedir senha de novo). */
export function preferenciasDaSessao(nickname: string): Preferencias | null {
  const conta = obterConta(nickname);
  if (!conta) return null;
  return contaParaPreferencias(conta);
}

export function salvarPreferenciasNaConta(nickname: string, preferencias: Preferencias): void {
  const chave = slugNickname(nickname);
  const banco = lerBanco();
  const conta = banco.contas[chave];
  if (!conta) return;
  conta.preferencias = normalizarPreferenciasConta({
    tema: preferencias.tema,
    corTabuleiro: preferencias.corTabuleiro,
    somAtivo: preferencias.somAtivo,
    volume: preferencias.volume,
    reduzirMovimento: preferencias.reduzirMovimento,
    mostrarDestinos: preferencias.mostrarDestinos,
    nivelPreferido: preferencias.nivelPreferido,
    controlePreferido: preferencias.controlePreferido,
    minutosPersonalizados: preferencias.minutosPersonalizados,
    incrementoPersonalizado: preferencias.incrementoPersonalizado
  });
  conta.atualizadoEm = Date.now();
  banco.contas[chave] = conta;
  gravarBanco(banco);
}

export function historicoDaConta(nickname: string): PartidaArquivada[] {
  const conta = obterConta(nickname);
  if (!conta) return [];
  return Array.isArray(conta.historico) ? [...conta.historico] : [];
}

export function arquivarPartidaNaConta(partida: PartidaArquivada): void {
  const chave = slugNickname(partida.nomeJogador);
  const banco = lerBanco();
  const conta = banco.contas[chave];
  if (!conta) return;
  const lista = (Array.isArray(conta.historico) ? conta.historico : []).filter((p) => p.id !== partida.id);
  lista.unshift(partida);
  conta.historico = lista.slice(0, MAX_HISTORICO);
  conta.atualizadoEm = Date.now();
  banco.contas[chave] = conta;
  gravarBanco(banco);
}

/** Importa partidas do arquivo global antigo para a conta (primeira vez). */
export function migrarHistoricoLegado(nickname: string, partidas: PartidaArquivada[]): number {
  const chave = slugNickname(nickname);
  const banco = lerBanco();
  const conta = banco.contas[chave];
  if (!conta) return 0;
  const existentes = new Set((conta.historico ?? []).map((p) => p.id));
  let adicionadas = 0;
  for (const p of partidas) {
    if (existentes.has(p.id)) continue;
    conta.historico.push(p);
    existentes.add(p.id);
    adicionadas += 1;
  }
  if (adicionadas > 0) {
    conta.historico = conta.historico
      .sort((a, b) => b.salvaEm - a.salvaEm)
      .slice(0, MAX_HISTORICO);
    conta.atualizadoEm = Date.now();
    banco.contas[chave] = conta;
    gravarBanco(banco);
  }
  return adicionadas;
}
